import { create } from 'zustand';
import { QueryClient } from '@tanstack/react-query';
import ClientContextDetails from '../models/ClientContextDetails';
import { DropDownListOption } from '../models/DropDownListOption';
import { getClientContextDetails, getClientFamilyMembers } from '../core/api';
import { clientContextQueryKey, familyMembersQueryKey, staleTimeInTwoHours } from '../core/reactQueryCore';
import { getClientId, isLoggedIn } from '../core/localStorage';
import { getApiErrorMessage, isApiError } from '../lib/ApiError';

let queryClient: QueryClient;

export const initializeClientStore = (client: QueryClient) => {
	queryClient = client;
};

interface ClientContextState {
	clientDetails: ClientContextDetails | null;
	clientWithOwnerDetails: DropDownListOption<number>[];
	fetchClientDetails: () => Promise<string | null>; // Get from cached data or fetch from API
	refetchClientDetailsFromApi: () => Promise<string | null>; // Refetch from API
	hasNewPushedNotification: boolean;
	setHasNewPushedNotification: (hasNewPushedNotification: boolean) => void;
	profileLocked: boolean;
	setProfileLocked: (profileLocked: boolean) => void;
}

const useClientContextStore = create<ClientContextState>((set) => ({
	clientDetails: null,
	clientWithOwnerDetails: [],
	fetchClientDetails: async () => {
		if (!isLoggedIn()) return null;

		try {
			const [clientData, familyMembers] = await Promise.all([
				queryClient.fetchQuery({
					queryKey: clientContextQueryKey,
					queryFn: getClientContextDetails,
					staleTime: staleTimeInTwoHours
				}),
				queryClient.fetchQuery({
					queryKey: familyMembersQueryKey,
					queryFn: getClientFamilyMembers,
					staleTime: staleTimeInTwoHours // Should keep the same as in useClientFamilyMembers
				})
			]);
			set({ clientDetails: clientData });
			set({ hasNewPushedNotification: clientData.hasNewPushedNotification });
			set({ profileLocked: clientData.profileLocked });
			set({
				clientWithOwnerDetails: familyMembers
					?.map((m) => {
						return {
							text: `${m.firstName} ${m.lastName}`,
							value: m.clientId
						};
					})
					.concat([
						{
							text: `${clientData?.firstName} ${clientData?.lastName}`,
							value: Number(getClientId())
						}
					])
			});
			return null;
		} catch (e) {
			return isApiError(e) ? getApiErrorMessage(e) : e.message;
		}
	},
	refetchClientDetailsFromApi: async () => {
		if (!isLoggedIn()) return null;

		// TODO: Consider rendering loading state while refetching
		try {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: clientContextQueryKey }),
				queryClient.invalidateQueries({ queryKey: familyMembersQueryKey })
			]);
			// Update the clientDetails and clientWithOwnerDetails state after refetching
			const error: string | null = await useClientContextStore.getState().fetchClientDetails();
			return error;
		} catch (e) {
			return isApiError(e) ? getApiErrorMessage(e) : e.message;
		}
	},
	hasNewPushedNotification: false,
	setHasNewPushedNotification: (hasNewPushedNotification) => set({ hasNewPushedNotification }),
	profileLocked: false,
	setProfileLocked: (profileLocked) => set({ profileLocked })
}));

export default useClientContextStore;
